import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, BadgeCheck, HelpCircle, Flag, MessageCircle, CalendarClock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";

const WA = whatsappLink("34658790266", "Hola, quiero información sobre las prácticas en las pistas de examen DGT de Faro en Vigo");

const PHOTOS = [
  { src: "/assets/moto-kawasaki-z400-carnet-a2-vigo.webp", alt: "Alumno practicando con moto entre conos en las pistas de examen DGT de Autoescuelas Faro en Vigo", caption: "Circuito de destreza de moto (A1, A2 y A) en nuestras pistas" },
  { src: "/assets/cuadriciclo-ligero-carnet-aml-vigo.webp", alt: "Cuadriciclo ligero de Autoescuelas Faro en las pistas de examen de Vigo", caption: "Prácticas de cuadriciclo (AML) en pista" },
  { src: "/assets/furgoneta-remolque-carnet-be-vigo.webp", alt: "Vehículo con remolque practicando maniobras B+E en las pistas de Autoescuelas Faro en Vigo", caption: "Maniobras con remolque (B96 y B+E) en nuestras instalaciones" },
];

const PERMISOS_PISTA = [
  { code: "AM / AML", txt: "ciclomotor y cuadriciclo ligero: destreza completa en pista" },
  { code: "A1 · A2 · A", txt: "circuito cerrado de moto: zigzag, equilibrio, esquiva y frenada" },
  { code: "B96 · B+E", txt: "aparcamiento, marcha atrás, muelle y desenganche y reenganche con remolque" },
  { code: "C · C+E · D", txt: "maniobras de camión, tráiler y autobús" },
];

const FAQ = [
  { q: "¿Qué son las pistas de examen DGT de Faro?", a: "Son nuestras instalaciones de maniobras en Vigo, a las que la DGT acude a examinar. Allí se realizan las pruebas de destreza en circuito cerrado de moto (A1, A2, A), ciclomotor y cuadriciclo (AM, AML), remolque (B96, B+E) y las maniobras de los permisos profesionales." },
  { q: "¿De verdad me examino donde practico?", a: "Sí. Los alumnos de Faro entrenan las maniobras en el mismo trazado donde después se realiza la prueba: mismos conos, mismas distancias y mismas referencias. El día del examen no hay sorpresas." },
  { q: "¿Qué ventaja real supone?", a: "La prueba de destreza es la que más nervios genera porque se juega en detalles de centímetros. Conocer el trazado de memoria (dónde frenar, dónde girar la cabeza, qué referencia tomar en cada cono) multiplica las probabilidades de apto a la primera." },
  { q: "¿Dónde están las pistas?", a: "En Vigo. Al matricularte te indicamos el punto exacto y cómo llegar; las prácticas de pista se organizan con tu profesor y las motos, vehículos y remolques los ponemos nosotros." },
  { q: "¿Puedo practicar en pista si soy de Nigrán, Panxón o A Cañiza?", a: "Sí. Puedes matricularte y preparar la teórica en cualquiera de nuestros 5 centros; las prácticas de pista se realizan en Vigo con nuestros vehículos." },
  { q: "¿Cuántas prácticas de pista necesitaré?", a: "Depende del permiso y de tu soltura: tu profesor entrena contigo cada maniobra por separado y solo te presenta cuando las dominas. Consulta las faltas oficiales de cada prueba en nuestro selector de criterios DGT." },
];

export default function PistasExamenPage() {
  useSeo({
    title: "Pistas de Examen DGT en Vigo | Practica donde te examinas",
    description: "La DGT examina en las pistas de Autoescuelas Faro en Vigo: practica moto, cuadriciclo AML, remolque y maniobras en el mismo trazado del examen.",
    canonical: `${SITE_URL}/pistas-examen-dgt-vigo`,
  });

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="pistas-page">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Place",
        name: "Pistas de examen DGT de Autoescuelas Faro",
        description: "Instalaciones de maniobras en Vigo donde la DGT examina y los alumnos de Autoescuelas Faro practican.",
        address: { "@type": "PostalAddress", addressLocality: "Vigo", addressRegion: "Pontevedra", addressCountry: "ES" },
        url: `${SITE_URL}/pistas-examen-dgt-vigo`,
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)]" data-testid="pistas-back">
          <ArrowLeft size={16} /> Volver al inicio
        </Link>
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Practica donde te examinas</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
          Pistas de examen DGT de Autoescuelas Faro en Vigo
        </h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
          La DGT acude a <strong>nuestras propias pistas de maniobras</strong> a examinar. Eso significa que los alumnos de Faro
          entrenan el circuito de destreza en el <strong>mismo trazado, con los mismos conos y las mismas referencias</strong> que
          encontrarán el día del examen. Una ventaja que muy pocas autoescuelas pueden ofrecer.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={WA} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm" data-testid="pistas-cta-wa">
            <MessageCircle size={15} /> Quiero practicar en pista
          </a>
          <a href="/#contacto" className="btn-pill btn-outline text-sm" data-testid="pistas-cta-matricula">
            <CalendarClock size={15} /> Matricúlate
          </a>
        </div>

        <section className="mt-8 rounded-2xl bg-[var(--faro-blue)] text-white p-6" data-testid="pistas-respuesta-rapida">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">Respuesta rápida</div>
          <h2 className="font-display font-black text-xl mt-1">¿Dónde se hacen los exámenes de destreza en Vigo?</h2>
          <p className="text-sm text-white/85 mt-2">
            Las pruebas de circuito cerrado de moto, cuadriciclo y remolque de los alumnos de Autoescuelas Faro se realizan en las
            pistas propias de la autoescuela en Vigo, donde la DGT acude a examinar y donde los alumnos practican cada maniobra
            antes de la prueba.
          </p>
        </section>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pistas-photos">
          {PHOTOS.map((im) => (
            <figure key={im.src} className="rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-white">
              <img src={im.src} alt={im.alt} loading="lazy" className="w-full h-48 object-cover" />
              <figcaption className="px-4 py-2.5 text-xs font-semibold text-[var(--faro-text-soft)]">{im.caption}</figcaption>
            </figure>
          ))}
        </div>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Qué permisos se examinan en nuestras pistas</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PERMISOS_PISTA.map((p) => (
              <div key={p.code} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5 flex items-start gap-3">
                <Flag size={18} className="text-[var(--faro-red)] shrink-0 mt-1" />
                <div>
                  <div className="font-display font-black text-[var(--faro-blue-deep)]">{p.code}</div>
                  <p className="text-sm text-[var(--faro-text-soft)] mt-1">{p.txt}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Por qué "practicar donde te examinas" marca la diferencia</h2>
          <ul className="mt-3 space-y-2 text-[var(--faro-text-soft)]">
            {[
              "Conoces el trazado de memoria: dónde frenar, qué referencia tomar en cada cono y cómo encarar cada maniobra.",
              "Entrenas sin tráfico y sin presión, repitiendo cada maniobra hasta que salga sola.",
              "El día del examen todo te resulta familiar: menos nervios, más aptos a la primera.",
              "Motos, cuadriciclo, remolques y vehículos profesionales propios: los mismos con los que te examinas.",
            ].map((b) => (
              <li key={b} className="flex gap-2">
                <BadgeCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Dónde matricularte</h2>
          <p className="mt-3 text-[var(--faro-text-soft)] flex items-start gap-2">
            <MapPin size={18} className="text-[var(--faro-red)] shrink-0 mt-1" />
            <span>
              Puedes matricularte en cualquiera de nuestros 5 centros:{" "}
              <Link to="/autoescuela-plaza-america" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Plaza América (Vigo)</Link>,{" "}
              <Link to="/autoescuela-coruxo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Coruxo</Link>,{" "}
              <Link to="/autoescuela-panxon" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Panxón</Link>,{" "}
              <Link to="/autoescuela-nigran" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Nigrán</Link> y{" "}
              <Link to="/autoescuela-a-caniza" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">A Cañiza</Link>.
              Las prácticas de pista se organizan en Vigo con tu profesor.
            </span>
          </p>
        </section>

        <section className="mt-10" id="faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <HelpCircle className="text-[var(--faro-red)]" size={26} /> Preguntas frecuentes
          </h2>
          <div className="mt-4 space-y-2" data-testid="pistas-faq">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-4 group">
                <summary className="cursor-pointer list-none font-bold text-[var(--faro-blue-deep)] flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[var(--faro-red)] group-open:rotate-90 transition-transform shrink-0"><ArrowRight size={16} /></span>
                </summary>
                <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-xl">Permisos que se preparan en estas pistas</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { label: "Carnet de moto (A1, A2, A)", to: "/carnet-moto-vigo" },
              { label: "Carnet A1", to: "/carnet-a1-vigo" },
              { label: "Carnet A2", to: "/carnet-a2-vigo" },
              { label: "Cuadriciclo AML", to: "/carnet-cuadriciclo-aml-vigo" },
              { label: "Remolque B96/B+E", to: "/carnet-remolque-vigo" },
              { label: "Camión (C)", to: "/carnet-camion-vigo" },
              { label: "Tráiler (C+E)", to: "/carnet-trailer-ce-vigo" },
              { label: "Autobús (D)", to: "/carnet-autobus-vigo" },
              { label: "Criterios de examen DGT", to: "/criterios-dgt" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="btn-pill btn-ghost text-sm" data-testid={`pistas-link${r.to.replace(/\//g, "-")}`}>
                {r.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
